import React, { memo } from "react";
import { useSelector } from "react-redux";

import { Paragraph } from "@tvg/design-system/web";
import { FilterOptions } from "@urp/lib-racetracks/src/types";
import { useRaceTracks, RESULTED_RACES_STATUS } from "@urp/lib-racetracks";
import { NO_TRACKS_FILTER } from "@urp/lib-racetracks/src/constants";
import { getIsLogged } from "@urp/store-selectors";
import {
  getIsAccountCompliant,
  getFavoriteTracks
} from "@urp/lib-racetracks/src/redux/selectors";
import { getFavoriteTracksInfo } from "../../redux/selectors";
import { favoriteTracksInfo } from "../../redux/defaultValues";
import FavoriteTracksList from "../FavoriteTracksList";
import { NoFavoritesWrapper } from "./styled-components";
import { buildQaLabel } from "../../utils";
import {
  CONTAINER,
  EMPTY,
  FAVORITE,
  HINT,
  LIST,
  TITLE,
  TRACK
} from "../../utils/constants";
import EmptyMessage from "../EmptyMessage";
import TracksSection from "../TracksSection";
import FavoriteTracksMask from "./FavoriteTracksMask";

interface FavoriteTracksProps {
  isResults?: boolean;
  filterOptions?: FilterOptions;
}

const FavoriteTracks = ({
  isResults = false,
  filterOptions
}: FavoriteTracksProps) => {
  const isUserLogged = useSelector(getIsLogged);
  const isAccountCompliant = useSelector(getIsAccountCompliant);
  const favoriteTracks = useSelector(getFavoriteTracks);

  const { title = favoriteTracksInfo.title, noFavorites } = useSelector(
    getFavoriteTracksInfo
  );
  const {
    tracksRaces: favoriteTracksRaces,
    onTrackClickHandler,
    isSearchOrFilterApplied,
    loading
  } = useRaceTracks({
    ...filterOptions,
    raceStatusFilter: isResults ? RESULTED_RACES_STATUS : [],
    tracksFilter: favoriteTracks?.length > 0 ? favoriteTracks : NO_TRACKS_FILTER
  });

  if (loading || isUserLogged === undefined) {
    return <FavoriteTracksMask />;
  }
  if (!isUserLogged || !isAccountCompliant) {
    return null;
  }

  if (
    !favoriteTracks.length ||
    (!favoriteTracksRaces.length && !isSearchOrFilterApplied)
  ) {
    return (
      <NoFavoritesWrapper
        data-qa-label={buildQaLabel([FAVORITE, EMPTY, CONTAINER])}
      >
        <Paragraph
          qaLabel={buildQaLabel([FAVORITE, EMPTY, TITLE])}
          fontFamily="bold"
        >
          {noFavorites.title}
        </Paragraph>
        <Paragraph qaLabel={buildQaLabel([FAVORITE, EMPTY, HINT])}>
          {noFavorites.hint}
        </Paragraph>
      </NoFavoritesWrapper>
    );
  }

  if (!favoriteTracksRaces.length && isSearchOrFilterApplied) {
    return (
      <TracksSection
        title={title}
        qaLabel={buildQaLabel([buildQaLabel([FAVORITE, TRACK]), LIST])}
      >
        <EmptyMessage isTracksSearchActive={isSearchOrFilterApplied} />
      </TracksSection>
    );
  }

  return (
    <FavoriteTracksList
      title={title}
      tracks={favoriteTracksRaces}
      onTrackClickHandler={onTrackClickHandler}
      qaLabel={buildQaLabel([FAVORITE, TRACK])}
    />
  );
};

export default memo(FavoriteTracks);
